import './legal-entity-leasings.scss';

import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import SearchInput from '@/components/Shared/Input/SearchInput';
import {
  ClientOtpModal,
  SystemNotifications
} from '@/components/Shared/entities';
import { ROUTES } from '@/components/Shared/routes/routes';
import { Select, Table } from '@/components/Shared/ui';
import { CREATE_ROUTE_NAME } from '@/constants';
import { useAppDispatch, useAppSelector } from '@/store';
import { setHeaderConfig } from '@/store/app.slice';

import { useFetchLeasingRequests } from '../hooks';
import type { TLeasingOrder } from '../types';
import { MobileCard, tableColumns } from './entities';
import { LeasingHeader } from './feature';
import { useValidateIeLeasingOtp } from './hooks';

const LegalEntityLeasings = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { userInfo } = useAppSelector((state) => state.userInfo);

  const {
    getRequestsPerPage,
    handleLegalEntityTypeChange,
    handlePageChange,
    handleUserChange,
    isLoading,
    legalEntityType,
    paginationParams,
    requests,
    selectedUser,
    total
  } = useFetchLeasingRequests({ requestType: 'legal_entity' });

  const {
    onClientSubmit,
    onOtpSubmit,
    retryOTP,
    isValidateModalOpened,
    setIsValidateModalOpened
  } = useValidateIeLeasingOtp({ getRequestsPerPage });

  const userSelectOptions = [
    { value: userInfo?.uuid || '', label: 'Мои заявки' },
    { value: 'all', label: 'Все заявки' }
  ];

  const legalEntityTypeOptions = [
    { value: '', label: 'Все' },
    { value: 'ip', label: 'ИП' },
    { value: 'too', label: 'ТОО' }
  ];

  const handleRowClick = (r: TLeasingOrder) => {
    if (r.isClientIpOwner) {
      if (r.status.code === 'leasing_draft') {
        navigate(ROUTES.leasingIeRequest(CREATE_ROUTE_NAME), {
          state: { requestId: r.uuid }
        });
      } else navigate(ROUTES.leasingIeRequest(r.uuid));
    } else {
      if (r.status.code === 'leasing_draft') {
        navigate(ROUTES.leasingLlpRequest(CREATE_ROUTE_NAME), {
          state: { requestId: r.uuid }
        });
      } else navigate(ROUTES.leasingLlpRequest(r.uuid));
    }
  };

  useEffect(() => {
    dispatch(
      setHeaderConfig({
        title: 'Лизинг',
        helmetTitle: 'Лизинг - Юр. лица'
      })
    );
    return () => {
      dispatch(setHeaderConfig({}));
    };
  }, []);

  return (
    <>
      <SystemNotifications />

      <div className="legal-entity-leasings">
        <LeasingHeader
          onCreateForIeClick={() =>
            setIsValidateModalOpened({ openedModal: 'client-info-form' })
          }
          onCreateForLlpClick={() =>
            navigate(ROUTES.leasingLlpRequest(CREATE_ROUTE_NAME))
          }
        />
        <div className="table_editor">
          <div className="table_editor__input">
            <SearchInput placeholder="Поиск по Наименование / БИН" />
          </div>
          <div className="table_editor__select">
            <Select
              placeholder="Тип заявки"
              selectedValue={legalEntityTypeOptions.find(
                (item) => item.value === legalEntityType
              )}
              onChange={({ value }) => handleLegalEntityTypeChange(value)}
              options={legalEntityTypeOptions}
            />
          </div>
          <div className="table_editor__select">
            <Select
              placeholder="Все заявки"
              selectedValue={userSelectOptions.find(
                (item) => item.value === selectedUser
              )}
              onChange={({ value }) => handleUserChange(value)}
              options={userSelectOptions}
            />
          </div>
        </div>
        <div className="table_editor__items">
          <Table
            columns={tableColumns}
            dataSource={requests}
            loading={isLoading}
            rowKey={(record) => record?.uuid || ''}
            pagination={{
              total,
              pageSize: paginationParams.limit,
              current: paginationParams.page,
              onChange: handlePageChange
            }}
            onRowClick={handleRowClick}
          />
        </div>
        <div className="table_editor__mobile-data">
          <MobileCard
            data={requests}
            pagination={{
              current: paginationParams.page,
              pageSize: paginationParams.limit,
              total
            }}
            onPageChange={handlePageChange}
            onClick={handleRowClick}
          />
        </div>
      </div>

      <ClientOtpModal
        title="Создание заявки"
        subtitle="Заполните данные о клиенте"
        onClientSubmit={onClientSubmit}
        onOtpSubmit={onOtpSubmit}
        onSMSRetry={(phone) => retryOTP({ phone, type: 'SMS' })}
        openedModal={isValidateModalOpened.openedModal}
        setOpenedModal={(modal) =>
          setIsValidateModalOpened({ openedModal: modal })
        }
        onClose={() => setIsValidateModalOpened({ openedModal: '' })}
        defaultPhone={isValidateModalOpened.defaultPhone}
      />
    </>
  );
};

export default LegalEntityLeasings;
